import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  interpolateColors,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { useCopy } from "../copy";
import { STORY_GAIN } from "../data/brands";
import { FLOAT_SHADOW, LOOK, TYPE } from "../look";
import { Struck, Tap } from "../ui/bits";
import { useStage } from "../stage";
import { Words } from "../ui/Words";
import { Sfx } from "../../b2b/ui/Sfx";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Cash, not points (the /vs Honey + ReferralCandy argument and the blog's
 * "cash vs points" post), proven on the spot: the wallet balance goes to
 * the bank in one tap. Mirrors the app's real transfer screen
 * (Porte-monnaie Frak → IBAN •••• 154).
 */

const CARD_AT = 26;
/** Transfer button centre, from the card's top edge. */
const BTN_FROM_TOP = 406;
/** Headline top → card top (square: 92 → 430). */
const HEAD_TO_CARD = 338;
const HEAD_H = 230;
const CARD_H = 500;
const STRIKE_AT = 20;
const TAP_AT = 58;
const DONE_AT = 62;

const Row: React.FC<{
  icon: React.ReactNode;
  title: string;
  meta: string;
  right?: React.ReactNode;
}> = ({ icon, title, meta, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 24,
      padding: "22px 26px",
      borderRadius: 30,
      background: LOOK.mist,
    }}
  >
    <div
      style={{
        width: 76,
        height: 76,
        borderRadius: 22,
        flex: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: LOOK.white,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, lineHeight: 1.15 }}>
      <div style={{ fontFamily: LOOK.body, fontWeight: LOOK.semi, fontSize: 34, color: LOOK.ink }}>
        {title}
      </div>
      <div style={{ fontFamily: LOOK.body, fontSize: 28, color: LOOK.inkSoft }}>{meta}</div>
    </div>
    {right}
  </div>
);

const BankIcon: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 10h18M5 10v8m4.5-8v8m5-8v8M19 10v8M3 20h18M12 3l9 5H3l9-5Z"
      stroke={LOOK.blue}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Cash: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();
  const wide = stage.shape === "wide";
  // Wide: a bit narrower so it clears the headline column.
  const CARD_W = wide ? 720 : 800;

  // Square: headline on top, card below (the master). Tall: the same
  // group, centred. Wide: headline left, card right.
  let headTop = 92;
  let cardTop = 430;
  let cardCX = stage.w / 2;
  if (stage.shape === "tall") {
    headTop = (stage.h - (HEAD_TO_CARD + CARD_H)) / 2;
    cardTop = headTop + HEAD_TO_CARD;
  } else if (wide) {
    headTop = (stage.h - HEAD_H) / 2;
    cardTop = (stage.h - CARD_H) / 2;
    cardCX = stage.w - 150 - (CARD_W + 64) / 2;
  }
  const slideFrom = stage.h - cardTop + 50;

  const card = spring({
    frame: frame - CARD_AT,
    fps,
    config: { damping: 17, stiffness: 170, mass: 0.8 },
  });
  const done = interpolate(frame, [DONE_AT, DONE_AT + 6], [0, 1], clamp);
  const press = interpolate(frame, [TAP_AT - 2, TAP_AT + 1, TAP_AT + 6], [1, 0.95, 1], clamp);
  const check = spring({ frame: frame - DONE_AT, fps, config: { damping: 10, stiffness: 260, mass: 0.5 } });
  // Balance drains to zero as the money leaves for the bank.
  const balance = interpolate(frame, [DONE_AT, DONE_AT + 14], [16 + STORY_GAIN, 0], {
    ...clamp,
    easing: Easing.bezier(0.33, 1, 0.68, 1),
  });

  return (
    <AbsoluteFill style={{ background: LOOK.white, alignItems: wide ? "flex-start" : "center" }}>
      <div
        style={{
          marginTop: headTop,
          marginLeft: wide ? 170 : 0,
          display: "flex",
          flexDirection: "column",
          alignItems: wide ? "flex-start" : "center",
        }}
      >
        <Words
          text={copy.cash.line1}
          fontSize={TYPE.headline + 8}
          color={LOOK.blue}
          align={wide ? "left" : "center"}
        />
        <div
          style={{
            display: "flex",
            gap: "0.26em",
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: TYPE.headline + 8,
            letterSpacing: -3.5,
            lineHeight: 1.1,
            color: LOOK.ink,
            opacity: interpolate(frame, [8, 12], [0, 1], clamp),
            translate: `0 ${interpolate(frame, [8, 18], [40, 0], { ...clamp, easing: Easing.bezier(0.16, 1, 0.3, 1) })}px`,
          }}
        >
          <span>{copy.cash.line2}</span>
          <Struck at={STRIKE_AT} thickness={14}>
            {copy.cash.struck}
          </Struck>
        </div>
      </div>

      {/* Transfer card */}
      <div
        style={{
          position: "absolute",
          top: cardTop,
          left: cardCX,
          width: CARD_W,
          padding: 30,
          borderRadius: 52,
          background: LOOK.white,
          boxShadow: FLOAT_SHADOW,
          border: `2px solid ${LOOK.mist}`,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          // Centred on cardCX whatever the padding/border (content-box).
          translate: `-50% ${(1 - card) * slideFrom}px`,
        }}
      >
        <Row
          icon={<FrakLogo variant="icon" width={44} />}
          title={copy.cash.walletName}
          meta={copy.cash.walletMeta}
          right={
            <div
              style={{
                fontFamily: LOOK.display,
                fontWeight: LOOK.bold,
                fontSize: 50,
                letterSpacing: -1.5,
                color: LOOK.ink,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {copy.money(balance)}
            </div>
          }
        />
        <div style={{ textAlign: "center", fontSize: 34, color: LOOK.muted, lineHeight: 0.8 }}>↓</div>
        <Row icon={<BankIcon />} title="IBAN" meta="•••• 154" />
        <div
          style={{
            marginTop: 10,
            height: 104,
            borderRadius: 104,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 18,
            background: interpolateColors(done, [0, 1], [LOOK.blue, LOOK.green]),
            color: LOOK.white,
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 38,
            scale: press,
          }}
        >
          {done < 0.5 ? (
            <>
              <span style={{ fontSize: 40 }}>⇄</span>
              {copy.cash.button}
            </>
          ) : (
            <>
              <svg width="46" height="46" viewBox="0 0 24 24" style={{ scale: check }}>
                <circle cx="12" cy="12" r="11" fill={LOOK.white} />
                <path
                  d="M7 12.5l3.2 3.2L17 9"
                  stroke={LOOK.green}
                  strokeWidth="2.6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {copy.cash.done}
            </>
          )}
        </div>
      </div>

      <Tap x={cardCX} y={cardTop + BTN_FROM_TOP + (1 - card) * slideFrom} at={TAP_AT} />

      <Sfx sound="strike" at={STRIKE_AT + 2} />
      <Sfx sound="click" at={TAP_AT} />
      <Sfx sound="confirm" at={DONE_AT} />
    </AbsoluteFill>
  );
};
